import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AI_QUEUE_STATUS } from '../utilities/aiRequestQueue.js';

const STATUS_LABELS = {
  [AI_QUEUE_STATUS.PENDING]: 'Waiting',
  [AI_QUEUE_STATUS.AWAITING_CONFIRMATION]: 'Needs confirmation',
  [AI_QUEUE_STATUS.RUNNING]: 'Working',
  [AI_QUEUE_STATUS.AWAITING_REVIEW]: 'Needs review',
  [AI_QUEUE_STATUS.FAILED]: 'Needs attention',
};

const EDITABLE = new Set([
  AI_QUEUE_STATUS.PENDING,
  AI_QUEUE_STATUS.AWAITING_CONFIRMATION,
  AI_QUEUE_STATUS.FAILED,
]);

const AiAssistantQueue = ({
  requests,
  tasks,
  disabled = false,
  onUpdate,
  onRemove,
  onRetry,
  onMove,
  onMoveBy,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editTask, setEditTask] = useState('');
  const [editPrompt, setEditPrompt] = useState('');
  const [draggedId, setDraggedId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [canDrag, setCanDrag] = useState(false);
  const queueRef = useRef(null);
  const taskMap = useMemo(() => new Map(tasks.map((task) => [task.key, task])), [tasks]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(min-width: 768px) and (pointer: fine)');
    const update = () => setCanDrag(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const closeMenu = (event) => {
      if (event.key === 'Escape' || !queueRef.current?.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('pointerdown', closeMenu);
    document.addEventListener('keydown', closeMenu);
    return () => {
      document.removeEventListener('pointerdown', closeMenu);
      document.removeEventListener('keydown', closeMenu);
    };
  }, [openMenuId]);

  if (!requests.length) return null;

  const beginEdit = (request) => {
    setEditingId(request.id);
    setEditTask(request.task.type);
    setEditPrompt(request.prompt);
  };

  return (
    <section className="ai-assistant-queue" ref={queueRef}>
      <button
        className="ai-assistant-queue-toggle"
        type="button"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
        aria-controls="ai-assistant-queued-requests"
      >
        <span><i className="bi bi-list-task" aria-hidden="true"></i>Queued requests</span>
        <span className="ai-assistant-queue-toggle-meta">
          <span className="badge text-bg-primary">{requests.length}</span>
          <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true" />
        </span>
      </button>
      {expanded ? <ol className="ai-assistant-queue-list" id="ai-assistant-queued-requests">
        {requests.map((request, index) => {
          const task = taskMap.get(request.task.type);
          const editable = EDITABLE.has(request.status);
          const editing = editingId === request.id;
          const promptRequired = taskMap.get(editTask)?.promptRequired !== false;
          return (
            <li
              key={request.id}
              className={`ai-assistant-queue-item is-${request.status}`}
              draggable={canDrag && editable && !disabled}
              onDragStart={() => setDraggedId(request.id)}
              onDragEnd={() => setDraggedId(null)}
              onDragOver={(event) => { if (editable && draggedId) event.preventDefault(); }}
              onDrop={() => {
                if (draggedId && draggedId !== request.id) onMove(draggedId, request.id);
                setDraggedId(null);
              }}
            >
              {editing ? (
                <form onSubmit={(event) => {
                  event.preventDefault();
                  if (promptRequired && !editPrompt.trim()) return;
                  onUpdate(request.id, {
                    task: { ...request.task, type: editTask, key: editTask },
                    prompt: editPrompt,
                  });
                  setEditingId(null);
                  setOpenMenuId(null);
                }}>
                  <label>
                    <span>Task</span>
                    <select className="form-select" value={editTask}
                      onChange={(event) => setEditTask(event.target.value)}>
                      {tasks.map((option) => <option key={option.key} value={option.key}>
                        {option.label}
                      </option>)}
                    </select>
                  </label>
                  {promptRequired ? <label>
                    <span>Request</span>
                    <textarea className="form-control" rows="3" value={editPrompt}
                      onChange={(event) => setEditPrompt(event.target.value)} />
                  </label> : null}
                  <div className="ai-assistant-queue-edit-actions">
                    <button className="btn btn-sm btn-outline-secondary" type="button"
                      onClick={() => setEditingId(null)}>Cancel</button>
                    <button className="btn btn-sm btn-primary" type="submit"
                      disabled={promptRequired && !editPrompt.trim()}>Save request</button>
                  </div>
                </form>
              ) : (
                <>
                  <span className="ai-assistant-queue-order">{index + 1}</span>
                  <div className="ai-assistant-queue-copy">
                    <strong>{task?.label || request.task.label || request.task.type}</strong>
                    <span className="ai-assistant-queue-status">{STATUS_LABELS[request.status]}</span>
                    {editable ? (
                      <button
                        className="btn btn-sm btn-light ai-assistant-queue-more"
                        type="button"
                        onClick={() => setOpenMenuId((current) => (
                          current === request.id ? null : request.id
                        ))}
                        aria-label={`Request actions for ${task?.label || request.task.type}`}
                        aria-haspopup="menu"
                        aria-expanded={openMenuId === request.id}
                        disabled={disabled}
                      >
                        <i className="bi bi-three-dots" aria-hidden="true" />
                      </button>
                    ) : null}
                    {request.task.contextLabel ? <small>{request.task.contextLabel}</small> : null}
                    {request.prompt ? <p>{request.prompt}</p> : null}
                    {request.error ? <p className="text-danger">{request.error}</p> : null}
                  </div>
                  {editable && openMenuId === request.id ? (
                    <div className="ai-assistant-queue-actions" role="menu">
                      {request.status === AI_QUEUE_STATUS.FAILED ? (
                        <button className="btn btn-sm" type="button" role="menuitem"
                          onClick={() => { onRetry(request.id); setOpenMenuId(null); }} disabled={disabled}>
                          <i className="bi bi-arrow-clockwise" aria-hidden="true" />Retry
                        </button>
                      ) : null}
                      <button className="btn btn-sm" type="button" role="menuitem"
                        onClick={() => { onMoveBy(request.id, -1); setOpenMenuId(null); }}
                        disabled={disabled || index === 0}>
                        <i className="bi bi-arrow-up" aria-hidden="true" />Move up
                      </button>
                      <button className="btn btn-sm" type="button" role="menuitem"
                        onClick={() => { onMoveBy(request.id, 1); setOpenMenuId(null); }}
                        disabled={disabled || index === requests.length - 1}>
                        <i className="bi bi-arrow-down" aria-hidden="true" />Move down
                      </button>
                      <button className="btn btn-sm" type="button" role="menuitem"
                        onClick={() => { beginEdit(request); setOpenMenuId(null); }} disabled={disabled}>
                        <i className="bi bi-pencil" aria-hidden="true" />Edit
                      </button>
                      <button className="btn btn-sm text-danger" type="button" role="menuitem"
                        onClick={() => { onRemove(request.id); setOpenMenuId(null); }} disabled={disabled}>
                        <i className="bi bi-trash" aria-hidden="true" />Remove
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </li>
          );
        })}
      </ol> : null}
    </section>
  );
};

AiAssistantQueue.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.object).isRequired,
  tasks: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    promptRequired: PropTypes.bool,
  })).isRequired,
  disabled: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onMoveBy: PropTypes.func.isRequired,
};

export default AiAssistantQueue;
